import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import axios from '../../api/axios';
import toast from 'react-hot-toast';
import { formatCurrency } from '../../utils/currency';
import { useOrganization } from '../../hooks/useOrganization';
import VehicleSelector from './VehicleSelector';
import CustomerModal from './CustomerModal';
import VehicleDamageSketch from './VehicleDamageSketch';
import { useAuth } from '../../context/AuthContext';
import { STATUS_OPTIONS, canSelectStatus, getStatusProgressionMessage } from '../../utils/jobCardStatus';
import PreServiceChecklist from './PreServiceChecklist';

const EditJobCardForm = ({ jobCard, onClose, onSuccess }) => {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const [activeTab, setActiveTab] = useState('details');
  const [formData, setFormData] = useState({
    customerId: '',
    vehicleId: '',
    vehicleNumber: '',
    vinNumber: '',
    odometerReading: '',
    branchId: '',
    appointmentDate: '',
    appointmentTime: '',
    estimatedTime: '',
    comments: '',
    referral: '',
    warrantyDate: '',
    discount: 0,
    servicesMargin: 10,
    partsMargin: 15,
    status: 'PENDING',
    technicianId: ''
  });

  const [customers, setCustomers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [services, setServices] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [availableParts, setAvailableParts] = useState([]);
  const [parts, setParts] = useState([]);
  const [showVehicleSelector, setShowVehicleSelector] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [vatPercentage, setVatPercentage] = useState(5);
  const [damagePoints, setDamagePoints] = useState([]);
  const [checklist, setChecklist] = useState({});
  const [mechanicsComments, setMechanicsComments] = useState('');
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [newPhotos, setNewPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    fetchData();
    if (jobCard) {
      populateForm();
      fetchPhotos();
    }
  }, [jobCard]);

  const fetchPhotos = async () => {
    try {
      const response = await axios.get(`/api/jobcards/${jobCard.id}/photos`);
      setExistingPhotos(response.data.photos || []);
    } catch (error) {
    }
  };

  const populateForm = () => {
    const appointmentDate = jobCard.appointmentDate ? new Date(jobCard.appointmentDate) : null;
    
    setFormData({
      customerId: jobCard.customerId || '',
      vehicleId: jobCard.vehicleId || '',
      vehicleNumber: jobCard.vehicleNumber || '',
      vinNumber: jobCard.vinNumber || '',
      odometerReading: jobCard.odometerReading || '',
      branchId: jobCard.branchId || '',
      appointmentDate: appointmentDate ? appointmentDate.toISOString().split('T')[0] : '',
      appointmentTime: appointmentDate ? appointmentDate.toTimeString().slice(0, 5) : '',
      estimatedTime: jobCard.estimatedTime || '',
      comments: jobCard.comments || '',
      referral: jobCard.referral || '',
      warrantyDate: jobCard.warrantyDate ? new Date(jobCard.warrantyDate).toISOString().split('T')[0] : '',
      discount: jobCard.discount || 0,
      servicesMargin: jobCard.servicesMargin || 10,
      partsMargin: jobCard.partsMargin || 15,
      status: jobCard.status || 'PENDING',
      technicianId: jobCard.technicianId || ''
    });

    setSelectedServices(Array.isArray(jobCard.services) ? jobCard.services : []);
    setParts(Array.isArray(jobCard.parts) ? jobCard.parts : []);
    setSelectedVehicle(jobCard.vehicle);
    setDamagePoints(jobCard.damagePoints || []);
    setChecklist(jobCard.checklist || {});
    setMechanicsComments('');
  };

  const fetchData = async () => {
    try {
      const [customersRes, branchesRes, servicesRes, partsRes, orgRes, techniciansRes] = await Promise.all([
        axios.get('/api/customers'),
        axios.get('/api/branches'),
        axios.get('/api/services'),
        axios.get('/api/parts'),
        axios.get('/api/organization'),
        axios.get('/api/members?role=TECHNICIAN')
      ]);
      
      setCustomers(customersRes.data.customers || customersRes.data || []);
      setBranches(branchesRes.data.branches || branchesRes.data || []);
      setServices(servicesRes.data.services || servicesRes.data || []);
      setAvailableParts(partsRes.data.parts || partsRes.data || []);
      setTechnicians(techniciansRes.data.members || techniciansRes.data || []);
      
      // Set VAT percentage from organization settings
      const organization = orgRes.data;
      if (organization && organization.vatPercentage !== null && organization.vatPercentage !== undefined) {
        setVatPercentage(parseFloat(organization.vatPercentage));
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const appointmentDateTime = formData.appointmentDate && formData.appointmentTime 
        ? `${formData.appointmentDate}T${formData.appointmentTime}:00`
        : null;

      
      const vehicleId = selectedVehicle?.isNew ? null : (formData.vehicleId && formData.vehicleId !== '' ? parseInt(formData.vehicleId) : null);
      
      const submitData = {
        customerId: formData.customerId || null,
        vehicleId: vehicleId,
        selectedVehicle: selectedVehicle,
        vehicleNumber: formData.vehicleNumber || '',
        vinNumber: formData.vinNumber || '',
        odometerReading: formData.odometerReading || '',
        branchId: formData.branchId || null,
        appointmentDate: appointmentDateTime,
        estimatedTime: formData.estimatedTime ? parseInt(formData.estimatedTime) : null,
        comments: formData.comments || '',
        referral: formData.referral || '',
        warrantyDate: formData.warrantyDate || null,
        discount: parseFloat(formData.discount) || 0,
        servicesMargin: parseFloat(formData.servicesMargin) || 0,
        partsMargin: parseFloat(formData.partsMargin) || 0,
        services: selectedServices.filter(s => s.serviceId && !isNaN(parseInt(s.serviceId))).map(s => ({
          ...s,
          serviceId: parseInt(s.serviceId)
        })),
        parts: parts.filter(p => p.partId && !isNaN(parseInt(p.partId))).map(p => ({
          ...p,
          partId: parseInt(p.partId)
        })),
        damagePoints,
        checklist,
        status: formData.status,
        mechanicsComments: mechanicsComments.trim(),
        technicianId: formData.technicianId ? parseInt(formData.technicianId) : null
      };

      await axios.put(`/api/job-cards/${jobCard.id}`, submitData);
      
      // Auto-upload any pending new photos
      if (newPhotos.length > 0) {
        try {
          const photoFormData = new FormData();
          newPhotos.forEach(photo => photoFormData.append('photos', photo.file));
          const captions = newPhotos.map(p => p.caption || '');
          photoFormData.append('captions', JSON.stringify(captions));
          await axios.post(`/api/jobcards/${jobCard.id}/photos`, photoFormData);
          setNewPhotos([]);
        } catch (photoError) {
          toast.error('Job card saved but some photos failed to upload');
        }
      }
      
      toast.success('Job card updated successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update job card');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addService = () => {
    setSelectedServices(prev => [...(Array.isArray(prev) ? prev : []), { serviceId: '', quantity: 1, price: 0 }]);
  };

  const updateService = (index, field, value) => {
    const updated = [...(Array.isArray(selectedServices) ? selectedServices : [])];
    updated[index][field] = value;
    
    if (field === 'serviceId') {
      const service = services.find(s => s.id === value);
      if (service) updated[index].price = service.price || 0;
    }
    
    setSelectedServices(updated);
  };

  const removeService = (index) => {
    setSelectedServices(prev => Array.isArray(prev) ? prev.filter((_, i) => i !== index) : []);
  };

  const addPart = () => {
    setParts([...(Array.isArray(parts) ? parts : []), { partId: '', quantity: 1, price: 0 }]);
  };

  const updatePart = (index, field, value) => {
    const updated = [...(Array.isArray(parts) ? parts : [])];
    updated[index][field] = value;
    
    if (field === 'partId') {
      const part = availableParts.find(p => p.id === parseInt(value));
      if (part) {
        // Use B2B (wholesale) price if customer is B2B, otherwise use retail/default price
        const selectedCustomer = customers.find(c => c.id === parseInt(formData.customerId));
        const isB2B = selectedCustomer?.type === 'B2B';
        
        if (isB2B && parseFloat(part.wholesalePrice) > 0) {
          updated[index].price = parseFloat(part.wholesalePrice);
        } else if (!isB2B && parseFloat(part.retailPrice) > 0) {
          updated[index].price = parseFloat(part.retailPrice);
        } else {
          updated[index].price = parseFloat(part.price || 0);
        }
        updated[index].name = part.name;
        updated[index].partId = part.id;
      }
    }
    
    setParts(updated);
  };

  const removePart = (index) => {
    setParts((Array.isArray(parts) ? parts : []).filter((_, i) => i !== index));
  };

  const calculateTotals = () => {
    const servicesTotal = Array.isArray(selectedServices) ? selectedServices.reduce((sum, s) => sum + (s.price * s.quantity), 0) : 0;
    const partsTotal = Array.isArray(parts) ? parts.reduce((sum, p) => sum + (p.price * p.quantity), 0) : 0;
    const subtotal = servicesTotal + partsTotal;
    const discount = parseFloat(formData.discount) || 0;
    const vat = (subtotal - discount) * (vatPercentage / 100);
    const total = subtotal - discount + vat;
    
    return { servicesTotal, partsTotal, subtotal, discount, vat, total };
  };

  const totals = calculateTotals();

  const compressImage = (file, maxSizeMB = 1.5, maxWidth = 1920, maxHeight = 1920) => {
    return new Promise((resolve) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const reader = new FileReader();

      reader.onload = (e) => {
        img.onload = () => {
          let { width, height } = img;

          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          let quality = 0.6;
          const tryCompress = () => {
            canvas.toBlob((blob) => {
              if (blob.size > maxSizeMB * 1024 * 1024 && quality > 0.2) {
                quality -= 0.1;
                tryCompress();
              } else {
                const compressedFile = new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() });
                resolve(compressedFile);
              }
            }, 'image/jpeg', quality);
          };
          tryCompress();
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    if (existingPhotos.length + newPhotos.length + files.length > 10) {
      toast.error('Maximum 10 photos allowed');
      e.target.value = '';
      return;
    }
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        continue;
      }

      let processedFile = file;
      if (file.size > 1.5 * 1024 * 1024) {
        toast.loading(`Compressing ${file.name}...`, { id: 'compress' });
        processedFile = await compressImage(file);
        toast.dismiss('compress');
      }

      const reader = new FileReader();
      reader.onload = (ev) => {
        setNewPhotos(prev => [...prev, {
          file: processedFile,
          preview: ev.target.result,
          name: file.name,
          caption: ''
        }]);
      };
      reader.readAsDataURL(processedFile);
    }
    
    e.target.value = '';
  };

  const updateNewPhotoCaption = (index, caption) => {
    setNewPhotos(prev => prev.map((p, i) => i === index ? { ...p, caption } : p));
  };
  
  const removeNewPhoto = (index) => {
    setNewPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const deleteExistingPhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    
    try {
      await axios.delete(`/api/jobcards/${jobCard.id}/photos/${photoId}`);
      setExistingPhotos(prev => prev.filter(p => p.id !== photoId));
      toast.success('Photo deleted successfully');
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const uploadNewPhotos = async () => {
    if (newPhotos.length === 0) return;
    
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      newPhotos.forEach(photo => formData.append('photos', photo.file));
      const captions = newPhotos.map(p => p.caption || '');
      formData.append('captions', JSON.stringify(captions));
      
      await axios.post(`/api/jobcards/${jobCard.id}/photos`, formData);
      
      setNewPhotos([]);
      await fetchPhotos();
      toast.success('Photos uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload photos');
    } finally {
      setUploadingPhoto(false);
    }
  };



  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">Edit Job Card - {jobCard?.orderNo}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col h-full">
          {/* Tabs */}
          <div className="flex border-b px-6 pt-6">
            <button
              type="button"
              onClick={() => setActiveTab('details')}
              className={`px-4 py-2 font-medium text-sm border-b-2 ${activeTab === 'details' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              Job Details
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('sketch')}
              className={`px-4 py-2 font-medium text-sm border-b-2 ${activeTab === 'sketch' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              Vehicle Sketch
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('photos')}
              className={`px-4 py-2 font-medium text-sm border-b-2 ${activeTab === 'photos' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              Photos
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('checklist')}
              className={`px-4 py-2 font-medium text-sm border-b-2 ${activeTab === 'checklist' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              Checklist
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'details' && (
              <div className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Branch *</label>
              <select
                name="branchId"
                value={formData.branchId}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Branch</option>
                {branches.map(branch => (
                  <option key={branch.id} value={branch.id}>{branch.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer *</label>
              <div className="flex space-x-2">
                <select
                  name="customerId"
                  value={formData.customerId}
                  onChange={handleChange}
                  required
                  className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Customer</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name} - {customer.phone || customer.contact}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowCustomerModal(true)}
                  className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Technician</label>
              <select
                name="technicianId"
                value={formData.technicianId}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Technician</option>
                {technicians.map(technician => (
                  <option key={technician.id} value={technician.id}>
                    {technician.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Vehicle Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Number</label>
              <input
                type="text"
                name="vehicleNumber"
                value={formData.vehicleNumber}
                onChange={handleChange}
                placeholder="e.g., ABC-123"
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">VIN#</label>
              <input
                type="text"
                name="vinNumber"
                value={formData.vinNumber}
                onChange={handleChange}
                placeholder="Vehicle Identification Number"
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ODO Meter</label>
              <input
                type="text"
                name="odometerReading"
                value={formData.odometerReading}
                onChange={handleChange}
                placeholder="e.g., 50000 km"
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle</label>
              <button
                type="button"
                onClick={() => setShowVehicleSelector(true)}
                className="w-full px-3 py-2 border rounded-md text-left hover:bg-gray-50"
              >
                {selectedVehicle ? `${selectedVehicle.make || selectedVehicle.brand || 'Unknown'} ${selectedVehicle.model}` : 'Select Vehicle'}
              </button>
            </div>
          </div>

          {/* Appointment */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Appointment Date</label>
              <input
                type="date"
                name="appointmentDate"
                value={formData.appointmentDate}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
              <input
                type="time"
                name="appointmentTime"
                value={formData.appointmentTime}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Time (min)</label>
              <input
                type="number"
                name="estimatedTime"
                value={formData.estimatedTime}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Services */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium text-gray-700">Services</label>
              <button
                type="button"
                onClick={addService}
                className="flex items-center px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
              >
                <Plus className="w-3 h-3 mr-1" />
                Add Service
              </button>
            </div>
            {(Array.isArray(selectedServices) ? selectedServices : []).map((service, index) => (
              <div key={index} className="grid grid-cols-12 gap-2 mb-2">
                <div className="col-span-5">
                  <select
                    value={service.serviceId}
                    onChange={(e) => updateService(index, 'serviceId', e.target.value)}
                    className="w-full px-2 py-1 border rounded text-sm"
                  >
                    <option value="">Select Service</option>
                    {services.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    placeholder="Qty"
                    value={service.quantity}
                    onChange={(e) => updateService(index, 'quantity', parseInt(e.target.value))}
                    className="w-full px-2 py-1 border rounded text-sm"
                  />
                </div>
                <div className="col-span-3">
                  <input
                    type="number"
                    placeholder="Price"
                    value={service.price}
                    onChange={(e) => updateService(index, 'price', parseFloat(e.target.value))}
                    step="0.01"
                    className="w-full px-2 py-1 border rounded text-sm"
                  />
                </div>
                <div className="col-span-1">
                  <span className="text-sm">{formatCurrency(service.price * service.quantity, organization?.currency_code)}</span>
                </div>
                <div className="col-span-1">
                  <button
                    type="button"
                    onClick={() => removeService(index)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Parts */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium text-gray-700">Parts</label>
              <button
                type="button"
                onClick={addPart}
                className="flex items-center px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
              >
                <Plus className="w-3 h-3 mr-1" />
                Add Part
              </button>
            </div>
            {(Array.isArray(parts) ? parts : []).map((part, index) => (
              <div key={index} className="grid grid-cols-12 gap-2 mb-2">
                <div className="col-span-5">
                  <select
                    value={part.partId || ''}
                    onChange={(e) => updatePart(index, 'partId', e.target.value)}
                    className="w-full px-2 py-1 border rounded text-sm"
                  >
                    <option value="">Select Part</option>
                    {availableParts.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name} (Stock: {p.quantity})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    placeholder="Qty"
                    value={part.quantity}
                    onChange={(e) => updatePart(index, 'quantity', parseInt(e.target.value))}
                    className="w-full px-2 py-1 border rounded text-sm"
                  />
                </div>
                <div className="col-span-3">
                  <input
                    type="number"
                    placeholder="Price"
                    value={part.price}
                    onChange={(e) => updatePart(index, 'price', parseFloat(e.target.value))}
                    step="0.01"
                    className="w-full px-2 py-1 border rounded text-sm"
                  />
                </div>
                <div className="col-span-1">
                  <span className="text-sm">{formatCurrency(part.price * part.quantity, organization?.currency_code)}</span>
                </div>
                <div className="col-span-1">
                  <button
                    type="button"
                    onClick={() => removePart(index)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Status Update */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Job Status</label>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((status) => {
                const canSelect = canSelectStatus(status.value, jobCard?.status || 'PENDING', user?.role);
                
                return (
                  <button
                    key={status.value}
                    type="button"
                    onClick={() => canSelect && setFormData(prev => ({ ...prev, status: status.value }))}
                    disabled={!canSelect}
                    className={`px-4 py-2 text-white text-sm rounded-md transition-colors ${
                      formData.status === status.value 
                        ? status.color.replace('hover:', '') + ' ring-2 ring-offset-2 ring-gray-400'
                        : canSelect 
                          ? status.color + ' opacity-70'
                          : 'bg-gray-300 cursor-not-allowed'
                    }`}
                    title={!canSelect ? 'Cannot reverse job status. Contact admin/manager.' : ''}
                  >
                    {status.label}
                  </button>
                );
              })}
            </div>
            <div className="mt-2 text-xs text-gray-500">
              <p>{getStatusProgressionMessage(user?.role)}</p>
            </div>
          </div>

          {/* Mechanics Comments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mechanics Comments/Notes</label>
            <textarea
              value={mechanicsComments}
              onChange={(e) => setMechanicsComments(e.target.value)}
              rows={3}
              placeholder="Add work progress, issues found, parts replaced, or completion notes..."
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Additional Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">History</label>
              <textarea
                name="comments"
                value={formData.comments}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Referral</label>
                <input
                  type="text"
                  name="referral"
                  value={formData.referral}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Warranty Date</label>
                <input
                  type="date"
                  name="warrantyDate"
                  value={formData.warrantyDate}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>



                {/* Totals */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span>Services Total:</span>
                        <span>{formatCurrency(totals.servicesTotal, organization?.currency_code)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Parts Total:</span>
                        <span>{formatCurrency(totals.partsTotal, organization?.currency_code)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Subtotal:</span>
                        <span>{formatCurrency(totals.subtotal, organization?.currency_code)}</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span>Discount:</span>
                        <input
                          type="number"
                          name="discount"
                          value={formData.discount}
                          onChange={handleChange}
                          step="0.01"
                          className="w-20 px-2 py-1 border rounded text-right"
                        />
                      </div>
                      <div className="flex justify-between">
                        <span>VAT ({vatPercentage}%):</span>
                        <span>{formatCurrency(totals.vat, organization?.currency_code)}</span>
                      </div>
                      <div className="flex justify-between font-semibold">
                        <span>Total:</span>
                        <span>{formatCurrency(totals.total, organization?.currency_code)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'sketch' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Vehicle Damage Assessment</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Click on the vehicle diagram to mark damage points. You can add multiple points and describe each damage.
                </p>
                <div className="flex gap-6">
                  <div>
                    <VehicleDamageSketch
                      damagePoints={damagePoints}
                      onDamagePointsChange={setDamagePoints}
                    />
                  </div>
                  <div className="flex-1">
                    {damagePoints.length > 0 ? (
                      <div>
                        <h4 className="text-md font-medium text-gray-900 mb-3">Damage Points Summary</h4>
                        <div className="space-y-2">
                          {damagePoints.map((point, index) => (
                            <div key={index} className="p-3 bg-gray-50 rounded">
                              <div>
                                <span className="font-medium text-sm">Point {index + 1}:</span>
                                <span className="ml-2 text-sm">{point.label}</span>
                              </div>
                              <span className="inline-block mt-1 px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                                {point.damageType}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-gray-500 text-sm">
                        No damage points marked yet. Click on the vehicle diagram to add damage points.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'photos' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Job Card Photos</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Upload up to 10 photos (max 10MB each). Accepted formats: JPG, PNG, JPEG.
                </p>
                
                {/* Existing Photos */}
                {existingPhotos.length > 0 && (
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-3">Existing Photos ({existingPhotos.length})</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      {existingPhotos.map((photo) => (
                        <div key={photo.id} className="relative group">
                          <img
                            src={`${(window.ENV && window.ENV.REACT_APP_API_URL) || ''}${photo.filePath}`}
                            alt={`Photo ${photo.id}`}
                            className="w-full h-32 object-cover rounded-lg border"
                            onError={(e) => {
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => deleteExistingPhoto(photo.id)}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Upload Section */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Camera Capture (mobile-friendly) */}
                  <div className="border-2 border-dashed border-green-300 rounded-lg p-6 bg-green-50">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/jpg"
                      capture="environment"
                      onChange={handlePhotoUpload}
                      disabled={existingPhotos.length + newPhotos.length >= 10 || uploadingPhoto}
                      className="hidden"
                      id="photo-camera-edit"
                    />
                    <label
                      htmlFor="photo-camera-edit"
                      className={`cursor-pointer flex flex-col items-center justify-center space-y-2 ${
                        existingPhotos.length + newPhotos.length >= 10 || uploadingPhoto ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <div className="text-green-500">
                        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <p className="text-sm font-medium text-green-800">📷 Take Photo</p>
                    </label>
                  </div>

                  {/* File Upload (gallery) */}
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                    <input
                      type="file"
                      multiple
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handlePhotoUpload}
                      disabled={existingPhotos.length + newPhotos.length >= 10 || uploadingPhoto}
                      className="hidden"
                      id="photo-upload-edit"
                    />
                    <label
                      htmlFor="photo-upload-edit"
                      className={`cursor-pointer flex flex-col items-center justify-center space-y-2 ${
                        existingPhotos.length + newPhotos.length >= 10 || uploadingPhoto ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <div className="text-gray-400">
                        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                      </div>
                      <p className="text-sm font-medium text-gray-900">
                        {uploadingPhoto ? 'Uploading...' : '📁 Choose from Gallery'}
                      </p>
                    </label>
                  </div>
                </div>
                
                <p className="text-xs text-gray-500 text-center">
                  {existingPhotos.length + newPhotos.length}/10 photos
                </p>

                {/* New Photo Thumbnails */}
                {newPhotos.length > 0 && (
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-md font-medium text-gray-900">New Photos to Upload ({newPhotos.length})</h4>
                      <button
                        type="button"
                        onClick={uploadNewPhotos}
                        disabled={uploadingPhoto}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                      >
                        {uploadingPhoto ? 'Uploading...' : 'Upload Now'}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      {newPhotos.map((photo, index) => (
                        <div key={index} className="relative border rounded-lg overflow-hidden">
                          <img
                            src={photo.preview}
                            alt={photo.caption || `New Photo ${index + 1}`}
                            className="w-full h-36 object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => removeNewPhoto(index)}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                          <div className="p-2 bg-gray-50">
                            <input
                              type="text"
                              value={photo.caption || ''}
                              onChange={(e) => updateNewPhotoCaption(index, e.target.value)}
                              placeholder="Photo name / description"
                              className="w-full px-2 py-1 text-sm border rounded focus:ring-1 focus:ring-blue-500 focus:outline-none"
                            />
                            <p className="text-xs text-gray-400 mt-1">{(photo.file.size / 1024 / 1024).toFixed(1)}MB</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'checklist' && (
              <PreServiceChecklist checklist={checklist} onChange={setChecklist} />
            )}
          </div>

          {/* Submit */}
          <div className="flex justify-end space-x-3 p-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Updating...' : 'Update Job Card'}
            </button>
          </div>
        </form>
      </div>

      {/* Modals */}
      {showVehicleSelector && (
        <VehicleSelector
          customerId={formData.customerId}
          onSelect={(vehicle) => {
            setSelectedVehicle(vehicle);
            setFormData(prev => ({ 
              ...prev, 
              vehicleId: vehicle.isNew ? '' : String(vehicle.id)
            }));
            setShowVehicleSelector(false);
          }}
          onClose={() => setShowVehicleSelector(false)}
        />
      )}

      {showCustomerModal && (
        <CustomerModal
          onSuccess={(customer) => {
            setCustomers(prev => [...prev, customer]);
            setFormData(prev => ({ ...prev, customerId: customer.id }));
            setShowCustomerModal(false);
          }}
          onClose={() => setShowCustomerModal(false)}
        />
      )}
    </div>
  );
};

export default EditJobCardForm;
